if (!customElements.get('custom-subscription-widget')) {
  customElements.define(
    'custom-subscription-widget',
    class CustomSubscriptionWidget extends HTMLElement {
      connectedCallback() {
        if (this._lifeseedsSubInit) return;
        this._lifeseedsSubInit = true;

        this.planSource = this.dataset.planSource || 'auto';
        this.sectionId = this.dataset.sectionId;
        this.plansMount = this.querySelector('[data-lifeseeds-plans-mount]');
        this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
        this.quantityInput = this.querySelector('form input[name="quantity"]');
        this.minQtyInput = this.querySelector('[data-min-qty-input]');
        this.variantIdInput = this.querySelector('form input[name="id"]');
        this.ctaLabel = this.querySelector('[data-cta-label]');
        this.ctaSubscribeText = this.dataset.ctaSubscribe || 'Subscribe now';
        this.ctaOnetimeText = this.dataset.ctaOnetime || 'Buy now';
        this.stickyMount = document.querySelector('[data-sticky-plan-mount]');

        this.parseVariantPlanMap();

        this.boundDelegatedChange = (event) => {
          const t = event.target;
          if (!t || !t.matches || !t.matches('[data-card-input]')) return;
          this.applySelection(t);
        };
        this.addEventListener('change', this.boundDelegatedChange);

        this.boundOnVariantIdChange = this.onVariantIdChange.bind(this);
        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', this.boundOnVariantIdChange);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            const vid = event?.data?.variant?.id ?? this.variantIdInput?.value;
            if (vid) this.applyVariantToPlans(String(vid));
          });
        }

        this.refreshCardRefs();
        this.syncFromCheckedInput();
        if (this.planSource !== 'auto') {
          this.syncSellingPlanToVariant();
        }

        this._boundSealSubsSellingPlanChanged = (e) => this._onSealSubsSellingPlanChanged(e);
        document.addEventListener('sealsubs:selling_plan_changed', this._boundSealSubsSellingPlanChanged);

        // Capture-phase submit handler: when the selected plan carries free
        // gifts, we take over the add-to-cart with a multi-line request so the
        // gifts land as one-time line items alongside the subscription.
        // Bound on the widget element (an ancestor of the form) so it runs
        // during the capture phase, before the event reaches <product-form>'s
        // own handler on the form — letting us suppress it for the gift case
        // only. (Binding on the form itself would race product-form at the
        // target phase, where order is registration order, not capture-first.)
        this.boundGiftSubmit = (e) => this.onGiftAwareSubmit(e);
        this.addEventListener('submit', this.boundGiftSubmit, true);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.boundDelegatedChange);
        if (this._variantChangeUnsub) {
          this._variantChangeUnsub();
          this._variantChangeUnsub = null;
        }
        if (this.variantIdInput) {
          this.variantIdInput.removeEventListener('change', this.boundOnVariantIdChange);
        }
        if (this._boundSealSubsSellingPlanChanged) {
          document.removeEventListener('sealsubs:selling_plan_changed', this._boundSealSubsSellingPlanChanged);
        }
        if (this.boundGiftSubmit) {
          this.removeEventListener('submit', this.boundGiftSubmit, true);
        }
      }

      getSelectedCard() {
        const checked = this.inputs.find((input) => input.checked);
        return checked ? checked.closest('[data-card]') : null;
      }

      getSelectedGifts() {
        const card = this.getSelectedCard();
        const raw = card?.dataset?.cardGifts;
        if (!raw) return [];
        try {
          const arr = JSON.parse(raw);
          if (!Array.isArray(arr)) return [];
          return arr
            .map((g) => ({ id: Number(g && g.id), quantity: Number(g && g.quantity) || 1 }))
            .filter((g) => Number.isFinite(g.id) && g.id > 0 && g.quantity > 0);
        } catch {
          return [];
        }
      }

      onGiftAwareSubmit(evt) {
        const gifts = this.getSelectedGifts();
        // No gifts on this plan: let <product-form> handle the add normally.
        if (!gifts.length) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.addWithGifts(gifts);
      }

      addWithGifts(gifts) {
        const productForm = this.querySelector('product-form');
        const submitButton = this.querySelector('[type="submit"]');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const spinner = submitButton && submitButton.querySelector('.loading__spinner');

        if (submitButton) {
          if (submitButton.getAttribute('aria-disabled') === 'true') return;
          submitButton.setAttribute('aria-disabled', true);
          submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        }
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }

        const mainId = Number(this.variantIdInput?.value);
        const qty = parseInt(this.quantityInput?.value || '1', 10) || 1;
        const sellingPlan = (this.sellingPlanInput?.value || '').trim();
        // The gift quantities a merchant configures are what the card earns at
        // its own line quantity ("Double subscription" = 2 units, 2 jars), so
        // the ratio is gifts-per-those-units, not gifts-per-unit.
        const card = this.getSelectedCard();
        const baseUnits = Math.max(1, parseInt(card?.dataset?.lineQuantity || '1', 10) || 1);
        const planProperty = sellingPlan ? String(Number(sellingPlan)) : '0';

        // The gift path builds the line by hand instead of posting the form,
        // so the form's hidden properties[_min_qty] input never reaches the
        // cart on its own. Carry it over explicitly: the cart templates use it
        // as the quantity floor that stops a double-subscription line from
        // being dropped to 1 while keeping the double-sub price.
        const minQty = Math.max(1, parseInt(this.minQtyInput?.value || '', 10) || baseUnits);
        const mainItem = { id: mainId, quantity: qty, properties: { _min_qty: String(minQty) } };
        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);
        // Gifts are intentionally plain one-time line items (no selling_plan)
        // so they ship with the first order only and never recur. The _gift
        // property tells the cart UI to lock the line's quantity controls; the
        // _gift_for / _gift_plan / _gift_ratio trio lets assets/gift-sync.js
        // keep the gift quantity in step with the parent line afterwards.
        const giftItems = gifts.map((g) => ({
          id: g.id,
          quantity: Math.min(99, Math.max(1, Math.floor((g.quantity * qty) / baseUnits))),
          properties: {
            _gift: '1',
            _gift_for: String(mainId),
            _gift_plan: planProperty,
            _gift_ratio: `${g.quantity}:${baseUnits}`,
          },
        }));

        const postItems = (items) => {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          const body = { items };
          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
          }
          config.body = JSON.stringify(body);
          return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        };

        if (cart) cart.setActiveElement(document.activeElement);

        postItems([mainItem, ...giftItems])
          .then((response) => {
            // The multi-item add is all-or-nothing: a rejected gift line (e.g.
            // sold out) would otherwise block the subscription itself, so on
            // failure retry with the main item alone and drop the gifts.
            if (response.status && giftItems.length) return postItems([mainItem]);
            return response;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'custom-subscription-widget',
                productVariantId: mainId,
                errors: response.errors || response.description,
                message: response.message,
              });
              if (productForm && typeof productForm.handleErrorMessage === 'function') {
                productForm.handleErrorMessage(response.description);
              }
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            // Multi-item adds return { items: [...] }; renderContents expects
            // the added line's key/id at the top level to highlight it, so
            // surface the main subscription line (items[0]).
            const mainLine = Array.isArray(response.items) ? response.items[0] : null;
            if (mainLine) {
              if (response.key == null) response.key = mainLine.key;
              if (response.id == null) response.id = mainLine.id;
            }
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'custom-subscription-widget',
              productVariantId: mainId,
              cartData: response,
            });
            cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
              if (spinner) spinner.classList.add('hidden');
            }
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      parseVariantPlanMap() {
        const el = this.querySelector('script[type="application/json"][data-lifeseeds-sub-variant-plans]');
        try {
          this.variantPlanMap = el ? JSON.parse(el.textContent) : {};
        } catch {
          this.variantPlanMap = {};
        }
      }

      refreshCardRefs() {
        this.cards = Array.from(this.querySelectorAll('[data-card]'));
        this.inputs = Array.from(this.querySelectorAll('[data-card-input]'));
        this.buildStickySelect();
      }

      buildStickySelect() {
        if (!this.stickyMount) return;

        const select = document.createElement('select');
        select.setAttribute('data-sticky-plan-select', '');
        select.setAttribute('aria-label', this.ctaSubscribeText);

        this.cards.forEach((card) => {
          const input = card.querySelector('[data-card-input]');
          if (!input) return;
          const titleEl = card.querySelector('.lifeseeds-sub-widget__card-title');
          const priceEl = card.querySelector('.lifeseeds-sub-widget__price');
          const compareEl = card.querySelector('.lifeseeds-sub-widget__compare');
          const badgeEl = card.querySelector('.lifeseeds-sub-widget__card-badge');

          const option = document.createElement('option');
          option.value = input.value;
          option.textContent = titleEl ? titleEl.textContent.trim() : '';
          option.dataset.title = titleEl ? titleEl.textContent.trim() : '';
          option.dataset.price = priceEl ? priceEl.textContent.trim() : '';
          option.dataset.compare = compareEl ? compareEl.textContent.trim() : '';
          option.dataset.badge = badgeEl ? badgeEl.textContent.trim() : '';
          option.disabled = input.disabled;
          option.selected = input.checked;
          select.appendChild(option);
        });

        select.addEventListener('change', (event) => {
          const match = this.inputs.find((inp) => (inp.value || '') === event.target.value);
          if (match && !match.disabled) {
            match.checked = true;
            this.applySelection(match);
          }
        });

        const trigger = document.createElement('div');
        trigger.className = 'sticky-atc__plan-trigger';
        trigger.innerHTML =
          '<span class="sticky-atc__plan-lines">' +
          '<span class="sticky-atc__plan-row">' +
          '<span class="sticky-atc__plan-title" data-sticky-plan-title></span>' +
          '<span class="sticky-atc__plan-pricing">' +
          '<span class="sticky-atc__plan-compare" data-sticky-plan-compare></span>' +
          '<span class="sticky-atc__plan-price" data-sticky-plan-price></span>' +
          '</span>' +
          '</span>' +
          '<span class="sticky-atc__plan-badge" data-sticky-plan-badge></span>' +
          '</span>' +
          '<span class="sticky-atc__plan-caret" aria-hidden="true"></span>';
        trigger.appendChild(select);

        this.stickyMount.innerHTML = '';
        this.stickyMount.appendChild(trigger);

        this.updateStickyTrigger(select);
      }

      updateStickyTrigger(select) {
        if (!this.stickyMount) return;
        const opt = select.options[select.selectedIndex];
        const titleEl = this.stickyMount.querySelector('[data-sticky-plan-title]');
        const priceEl = this.stickyMount.querySelector('[data-sticky-plan-price]');
        const compareEl = this.stickyMount.querySelector('[data-sticky-plan-compare]');
        const badgeEl = this.stickyMount.querySelector('[data-sticky-plan-badge]');
        if (titleEl) titleEl.textContent = opt ? opt.dataset.title : '';
        if (priceEl) priceEl.textContent = opt ? opt.dataset.price : '';
        if (compareEl) {
          const compare = opt ? opt.dataset.compare : '';
          compareEl.textContent = compare;
          compareEl.hidden = !compare;
        }
        if (badgeEl) {
          const badge = opt ? opt.dataset.badge : '';
          badgeEl.textContent = badge;
          badgeEl.hidden = !badge;
        }
      }

      syncStickySelect(value) {
        const select = this.stickyMount && this.stickyMount.querySelector('[data-sticky-plan-select]');
        if (!select) return;
        select.value = value;
        this.updateStickyTrigger(select);
      }

      getAllowedSellingPlanIds() {
        const vid = this.variantIdInput?.value;
        if (!vid || !this.variantPlanMap) return [];
        const list = this.variantPlanMap[vid] ?? this.variantPlanMap[String(vid)];
        return Array.isArray(list) ? list.map(String) : [];
      }

      onVariantIdChange() {
        const vid = this.variantIdInput?.value;
        if (vid) this.applyVariantToPlans(String(vid));
      }

      applyVariantToPlans(variantId) {
        if (this.planSource === 'auto' && this.plansMount) {
          const tpl = document.getElementById(`lifeseeds-sub-plans-${this.sectionId}-${variantId}`);
          if (tpl) {
            this.plansMount.innerHTML = '';
            this.plansMount.appendChild(tpl.content.cloneNode(true));
            this.refreshCardRefs();
            this.syncFromCheckedInput();
            return;
          }
        }
        this.syncSellingPlanToVariant();
      }

      syncSellingPlanToVariant() {
        if (this.planSource === 'auto') return;

        const allowed = new Set(this.getAllowedSellingPlanIds());
        const checked = this.inputs.find((input) => input.checked);
        const checkedVal = (checked?.value ?? '').trim();

        if (checkedVal !== '' && !allowed.has(checkedVal)) {
          const onetime = this.inputs.find((inp) => (inp.value ?? '').trim() === '');
          if (onetime) {
            onetime.checked = true;
            this.applySelection(onetime);
          } else {
            const firstAllowed = this.inputs.find((inp) => {
              const v = (inp.value ?? '').trim();
              return v !== '' && allowed.has(v);
            });
            if (firstAllowed) {
              firstAllowed.checked = true;
              this.applySelection(firstAllowed);
            } else if (this.sellingPlanInput) {
              this.sellingPlanInput.value = '';
            }
          }
        }

        this.inputs.forEach((inp) => {
          const v = (inp.value ?? '').trim();
          if (v === '') return;
          const ok = allowed.has(v);
          inp.disabled = !ok;
          const card = inp.closest('[data-card]');
          if (card) card.classList.toggle('lifeseeds-sub-widget__card--disabled', !ok);
        });

        this.buildStickySelect();
      }

      syncFromCheckedInput() {
        const checked = this.inputs.find((input) => input.checked);
        if (checked) {
          this.applySelection(checked);
        }
      }

      _onSealSubsSellingPlanChanged(e) {
        const id = String((e.detail || {}).selling_plan_id ?? '');
        const match = this.inputs.find((inp) => String(inp.value) === id);
        if (!match || match.checked || match.disabled) return;

        if (this.planSource !== 'auto' && id !== '') {
          const allowed = new Set(this.getAllowedSellingPlanIds());
          if (!allowed.has(id)) return;
        }

        match.checked = true;
        this._syncingFromSealSubs = true;
        this.applySelection(match);
        this._syncingFromSealSubs = false;
      }

      applySelection(input) {
        const value = input.value || '';
        const card = input.closest('[data-card]');
        const cardType = card ? card.dataset.cardType : 'onetime';

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = value;
        }

        let qty = 1;
        const rawQty = card?.dataset?.lineQuantity;
        if (rawQty != null && String(rawQty).trim() !== '') {
          const parsed = parseInt(String(rawQty).trim(), 10);
          if (Number.isFinite(parsed) && parsed > 0) {
            qty = Math.min(99, parsed);
          }
        }
        if (this.quantityInput) {
          this.quantityInput.value = String(qty);
        }
        if (this.minQtyInput) {
          this.minQtyInput.value = String(qty);
        }

        this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));
        this.syncStickySelect(value);

        if (this.ctaLabel) {
          this.ctaLabel.textContent =
            cardType === 'subscription' ? this.ctaSubscribeText : this.ctaOnetimeText;
        }

        this.dispatchEvent(
          new CustomEvent('lifeseeds:subscription:planChanged', {
            bubbles: true,
            detail: {
              sellingPlanId: value,
              cardType,
              card,
            },
          })
        );

        if (!this._syncingFromSealSubs) {
          document.querySelectorAll('.sealsubs-target-element').forEach((widget) => {
            widget.dispatchEvent(
              new CustomEvent('sealsubs:change_selected_selling_plan_id', {
                detail: { selling_plan_id: value },
              })
            );
          });
        }
      }
    }
  );
}
